import { useEffect } from 'react'
import { Alert, Col, DatePicker, Divider, Form, Input, Modal, Row, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import type { OrgNode, OrgNodeType } from '@/features/org/orgApi'
import { OrgNodeTreeSelect } from './OrgNodeTreeSelect'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { Can } from '@/shared/auth/Can'
import {
  ADDRESS_MAX,
  EMPLOYMENT_TYPES,
  NAME_MAX,
  PERSONNEL_NO_PATTERN,
  REASON_MAX,
  createPerson,
  updatePerson,
  type EmploymentType,
  type JobTitle,
  type Person,
} from './hrApi'

/** `LocalDate` — vaqt zonasiz KUN. Server aynan shu shaklni kutadi. */
const DATE_FORMAT = 'YYYY-MM-DD'

interface PersonFormModalProps {
  open: boolean
  /** `null` = yangi xodim */
  person: Person | null
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  /** ⚠️ FAQAT faollari — o'chirilgan lavozimga yangi odam tayinlanmaydi */
  jobTitles: readonly JobTitle[]
  /** Tugunlar ro'yxati ishonchlimi (`useEmployeeNodes().reliable`) */
  nodesReliable: boolean
  onClose: () => void
}

interface FormValues {
  personnelNo: string
  lastName: string
  firstName: string
  middleName?: string
  phone?: string
  email?: string
  address?: string
  emergencyName?: string
  emergencyPhone?: string
  hiredAt: Dayjs
  // --- faqat YARATISHDA ---
  orgNodeId?: string
  jobTitleId?: string
  employmentType?: EmploymentType
  reason?: string
}

/**
 * Xodim yaratish va kadr yozuvini tahrirlash.
 *
 * <h2>⚠️ YARATISHDA TAYINLASH MAJBURIY, TAHRIRDA UMUMAN YO'Q</h2>
 * Ikkalasi ham ataylab.
 *
 * <b>Yaratishda</b> — `PersonCreate` tayinlashsiz so'rovni rad etadi va
 * sabab doira mexanizmida: `SUBTREE` filtri xodimni uning TUGUNI orqali
 * topadi. Tugunsiz xodim yaratilgan bo'lardi, lekin uni faqat `TENANT`
 * doiradagi odam ko'rardi — xato chiqmasdi, ro'yxat ishlardi, xodim
 * shunchaki YO'Q bo'lardi.
 *
 * <b>Tahrirda</b> — tugun va lavozim bu formadan CHIQARILGAN, chunki
 * ular kadr yozuvida umuman yashamaydi: ular tarixda
 * (`hr.employment_history`). Ularni bu yerga qo'yish "tahrirlash"
 * taassurotini berardi, mijoz esa aynan buni taqiqlagan
 * (<i>"muhim xodim ma'lumoti ustiga yozilmasin"</i>, §20.9-E).
 * Tayinlashni o'zgartirish — ALOHIDA amal va ALOHIDA huquq
 * (`hr.employment.write`).
 *
 * <h2>⚠️ TO'LIQ ISM MAYDONI YO'Q</h2>
 * `full_name` bazada hosila ustun (`GENERATED ALWAYS ... STORED`) va
 * §20.6 uni OBRO'LI MANBA deb belgilagan. Kiritish maydoni sifatida
 * qo'shsak, tarkibiy qismlar bilan ajralib ketardi.
 *
 * <h2>⚠️ SANA — KUN, LAHZA EMAS</h2>
 * `hiredAt` `LocalDate`: kadr buyrug'i kun bilan ishlaydi.
 * `Asia/Kabul` UTC+4:30 (§19.1) tufayli timestamp'da "qaysi kun"
 * savoli vaqt zonasiga bog'liq bo'lib qolardi — shuning uchun bu yerda
 * hech qanday zona konvertatsiyasi YO'Q, operator tanlagan kun aynan
 * shu holda yuboriladi.
 */
export function PersonFormModal({
  open,
  person,
  nodes,
  types,
  jobTitles,
  nodesReliable,
  onClose,
}: PersonFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const isEdit = person !== null

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      person
        ? {
            personnelNo: person.personnelNo,
            lastName: person.lastName,
            firstName: person.firstName,
            middleName: person.middleName ?? '',
            phone: person.phone ?? '',
            email: person.email ?? '',
            address: person.address ?? '',
            emergencyName: person.emergencyName ?? '',
            emergencyPhone: person.emergencyPhone ?? '',
            hiredAt: dayjs(person.hiredAt, DATE_FORMAT),
          }
        : // ⚠️ Tayinlash maydonlari ham ANIQ tozalanadi: `useForm`
          // instansiyasi modal yopilganda tirik qoladi va oldingi
          // xodimning tuguni keyingisiga jimgina meros bo'lardi.
          {
            personnelNo: '',
            lastName: '',
            firstName: '',
            middleName: '',
            phone: '',
            email: '',
            address: '',
            emergencyName: '',
            emergencyPhone: '',
            // ⚠️ TENANT kalendaridagi bugun (`dayRange.ts`)
            hiredAt: tenantToday(),
            orgNodeId: undefined,
            jobTitleId: undefined,
            employmentType: 'FULL_TIME',
            reason: '',
          },
    )
  }, [open, person, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const blank = (value: string | undefined): string | null => {
        const trimmed = (value ?? '').trim()
        return trimmed === '' ? null : trimmed
      }

      const draft = {
        personnelNo: values.personnelNo.trim(),
        lastName: values.lastName.trim(),
        firstName: values.firstName.trim(),
        middleName: blank(values.middleName),
        phone: blank(values.phone),
        email: blank(values.email),
        address: blank(values.address),
        emergencyName: blank(values.emergencyName),
        emergencyPhone: blank(values.emergencyPhone),
        hiredAt: values.hiredAt.format(DATE_FORMAT),
        // ⚠️ Panel hisob bog'lashni QO'LLAB-QUVVATLAMAYDI: `appUserRef`
        // `identity.app_user.id` va uni tanlash uchun foydalanuvchilar
        // ro'yxati kerak — u boshqa huquq ostida (`user.read`).
        // Tahrirda MAVJUD qiymat saqlanadi, yaratishda `null` (odatiy
        // hol: qorovul, oshpaz, haydovchida panel hisobi yo'q).
        appUserRef: person?.appUserRef ?? null,
      }

      if (isEdit && person) {
        return updatePerson(person.id, draft)
      }

      // ⚠️ ISH TURI uchun `?? 'FULL_TIME'` ATAYLAB YO'Q. Tugun va
      // lavozimda bo'sh qiymat serverga `''` bo'lib ketadi va server
      // uni RAD ETADI — ya'ni xato ko'rinadi. Ish turida esa
      // "odatiy qiymat" qo'yish serverga BOSHQA, lekin YAROQLI
      // qiymat yuborardi: xato chiqmasdi, xodim yaratilardi, kadr
      // yozuvi esa jimgina noto'g'ri bo'lardi.
      //
      // Maydonda boshlang'ich qiymat doim bor va `rules` uni majburiy
      // qiladi, ya'ni bu shart amalda bajarilmaydi. Bajarilsa —
      // forma buzilgan va buni KO'RISH kerak.
      if (values.employmentType === undefined) {
        throw new Error(t('hr.assignment.validation.typeRequired'))
      }

      return createPerson(draft, {
        // ⚠️ `!` ishlatilmaydi — maydonlar `rules` bilan majburiy
        // qilingan, lekin tip darajasida kafolat yo'q. Bo'sh qiymat
        // serverga ketmasligi uchun aniq tekshiriladi.
        orgNodeId: values.orgNodeId ?? '',
        jobTitleId: values.jobTitleId ?? '',
        employmentType: values.employmentType,
        // ⚠️ `null` — server `hiredAt` ni qo'yadi. Ular ikki xil
        // bo'lishi MUMKIN (ishga qabul qilindi, bo'limga keyinroq
        // biriktirildi), lekin odatiy holat bir xil.
        validFrom: null,
        reason: (values.reason ?? '').trim(),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const activeJobTitles = jobTitles.filter((title) => title.active)

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('hr.person.editTitle') : t('hr.person.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      okButtonProps={{ disabled: !isEdit && !nodesReliable }}
      destroyOnHidden
      width={720}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        {isEdit ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('hr.person.assignmentLockedNote')}
          />
        ) : null}

        {/*
          ⚠️ Tugunlar ro'yxati yo'q bo'lsa forma OCHIQ qoladi, lekin
          saqlash tugmasi o'chiriladi va SABABI yoziladi. Modalni umuman
          ochmaslik ham, sababsiz xato ham operatorni "nega ishlamadi?"
          holatida qoldirardi.
        */}
        {!isEdit && !nodesReliable ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('hr.person.nodesUnavailable')}
          />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="personnelNo"
              label={t('hr.person.personnelNo')}
              extra={t('hr.person.personnelNoHint')}
              rules={[
                { required: true, message: t('hr.person.validation.personnelNoRequired') },
                // `HrTextRules.PERSONNEL_NO` ning nusxasi — FAQAT raqam.
                { pattern: PERSONNEL_NO_PATTERN, message: t('hr.person.validation.personnelNoFormat') },
              ]}
            >
              <Input autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="lastName"
              label={t('hr.person.lastName')}
              rules={[
                { required: true, message: t('hr.person.validation.lastNameRequired') },
                { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="firstName"
              label={t('hr.person.firstName')}
              rules={[
                { required: true, message: t('hr.person.validation.firstNameRequired') },
                { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            {/* ⚠️ Ixtiyoriy: hamma madaniyatda otasining ismi yo'q */}
            <Form.Item
              name="middleName"
              label={t('hr.person.middleName')}
              rules={[{ max: NAME_MAX, message: t('hr.person.validation.nameLength') }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="phone" label={t('hr.person.phone')} rules={[{ max: 64 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="hiredAt"
              label={t('hr.person.hiredAt')}
              extra={t('hr.person.hiredAtHint')}
              rules={[{ required: true, message: t('hr.person.validation.hiredAtRequired') }]}
            >
              <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="email" label={t('hr.person.email')} rules={[{ max: 64 }]}>
          <Input />
        </Form.Item>

        <Can permission="hr.person.document.view">
          <Form.Item
            name="address"
            label={t('hr.person.address')}
            rules={[{ max: ADDRESS_MAX, message: t('hr.person.validation.addressLength') }]}
          >
            <Input.TextArea rows={2} maxLength={ADDRESS_MAX} />
          </Form.Item>
        </Can>

        {/*
          ⚠️ Favqulodda aloqa — IKKISI BIRGA yoki ikkisi ham bo'sh.
          Qoidani baza ham to'sadi (`person_emergency_pair`); bu yerdagi
          tekshiruv XABAR uchun: faqat ismi yozilgan yozuv favqulodda
          holatda foydasiz, lekin panelda "to'ldirilgan" bo'lib ko'rinardi.
        */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="emergencyName"
              label={t('hr.person.emergencyName')}
              dependencies={['emergencyPhone']}
              rules={[
                { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
                ({ getFieldValue }) => ({
                  validator: (_rule, value: string | undefined) => {
                    const phone = String(getFieldValue('emergencyPhone') ?? '').trim()
                    const name = (value ?? '').trim()
                    return phone !== '' && name === ''
                      ? Promise.reject(new Error(t('hr.person.validation.emergencyPair')))
                      : Promise.resolve()
                  },
                }),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="emergencyPhone"
              label={t('hr.person.emergencyPhone')}
              dependencies={['emergencyName']}
              rules={[
                { max: 64 },
                ({ getFieldValue }) => ({
                  validator: (_rule, value: string | undefined) => {
                    const name = String(getFieldValue('emergencyName') ?? '').trim()
                    const phone = (value ?? '').trim()
                    return name !== '' && phone === ''
                      ? Promise.reject(new Error(t('hr.person.validation.emergencyPair')))
                      : Promise.resolve()
                  },
                }),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {isEdit ? null : (
          <>
            <Divider orientation="left">{t('hr.assignment.sectionTitle')}</Divider>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('hr.assignment.firstNote')}
            />

            <Form.Item
              name="orgNodeId"
              label={t('hr.assignment.orgNode')}
              extra={t('hr.assignment.orgNodeHint')}
              rules={[{ required: true, message: t('hr.assignment.validation.nodeRequired') }]}
            >
              {/* ⚠️ `value`/`onChange` berilMAYDI — ularni `Form.Item` biriktiradi */}
              <OrgNodeTreeSelect nodes={nodes} types={types} />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="jobTitleId"
                  label={t('hr.assignment.jobTitle')}
                  rules={[{ required: true, message: t('hr.assignment.validation.jobTitleRequired') }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('hr.assignment.jobTitlePlaceholder')}
                    notFoundContent={t('hr.jobTitle.empty')}
                    options={activeJobTitles.map((title) => ({
                      value: title.id,
                      label: seededName('jobTitles', title.code, title.name, t),
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="employmentType"
                  label={t('hr.assignment.employmentType')}
                  rules={[{ required: true, message: t('hr.assignment.validation.typeRequired') }]}
                >
                  <Select
                    options={EMPLOYMENT_TYPES.map((type) => ({
                      value: type,
                      label: t(`employmentType.${type}`),
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="reason"
              label={t('hr.assignment.reason')}
              extra={t('hr.assignment.reasonHint')}
              rules={[
                { required: true, message: t('hr.assignment.validation.reasonRequired') },
                { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
              ]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}
